import React, { useRef, useState } from "react";
import { ReactComponent as Upload } from "../../../../assets/icons/upload-icon.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";

const DragDropUpload = ({
  label,
  multiple = false,
  accept = "",
  files = [],
  file = null,

  // existing saved files from Firebase
  existingFiles = [],
  existingFile = null,

  onChange,

  // called when deleting existing saved files
  onExistingChange,

  acceptedText = "Accepted file types: pdf, png, jpg",
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const selectedFiles = multiple ? files || [] : file ? [file] : [];
  const selectedExistingFiles = multiple
    ? existingFiles || []
    : existingFile
      ? [existingFile]
      : [];

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const mergeFiles = (currentFiles, newFiles) => {
    const fileMap = new Map();

    [...currentFiles, ...newFiles].forEach((item) => {
      const key = `${item.name}-${item.size}-${item.lastModified}`;
      fileMap.set(key, item);
    });

    return Array.from(fileMap.values());
  };

  const handleFiles = (fileList) => {
    const pickedFiles = Array.from(fileList || []);

    if (multiple) {
      const mergedFiles = mergeFiles(selectedFiles, pickedFiles);
      onChange?.(mergedFiles);
    } else {
      // In single mode, replacing file should also clear existing saved file
      onExistingChange?.(null);
      onChange?.(pickedFiles[0] || null);
    }

    resetInput();
  };

  const removeNewFile = (removeIndex) => {
    if (multiple) {
      const updatedFiles = selectedFiles.filter(
        (_, index) => index !== removeIndex,
      );
      onChange?.(updatedFiles);
    } else {
      onChange?.(null);
    }

    resetInput();
  };

  const removeExistingFile = (removeIndex) => {
    if (multiple) {
      const updatedExistingFiles = selectedExistingFiles.filter(
        (_, index) => index !== removeIndex,
      );
      onExistingChange?.(updatedExistingFiles);
    } else {
      onExistingChange?.(null);
    }

    resetInput();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  return (
    <div className="provider-network-upload-wrap">
      {label && (
        <label className="provider-network-label provider-network-file-label">
          {label}
        </label>
      )}

      <div
        className={`provider-network-drag-drop ${
          isDragging ? "is-dragging" : ""
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          className="provider-network-drag-drop-input"
          onChange={(e) => handleFiles(e.target.files)}
        />

        <div className="provider-network-drag-drop-content">
          <Upload className="provider-network-drag-drop-icon" />

          <span className="FP_Body">
            Drag &amp; drop {multiple ? "files" : "a file"} here or{" "}
            <button
              type="button"
              className="provider-network-browse-btn"
              onClick={openFilePicker}
            >
              <span className="Content-Bold-Underline">Browse</span>
            </button>
          </span>
        </div>
      </div>

      <small className="provider-network-accepted-text FP_Help_Text">
        {acceptedText}
      </small>

      {!!selectedExistingFiles.length && (
        <div className="provider-network-selected-files">
          {selectedExistingFiles.map((savedFile, index) => (
            <div
              key={`${savedFile.name || savedFile.url || savedFile.path}-${index}`}
              className="provider-network-selected-file-chip"
            >
              {savedFile.url && savedFile.type?.startsWith?.("image") && (
                <img
                  src={savedFile.url}
                  alt={savedFile.name || "Uploaded file"}
                  className="provider-network-selected-file-preview"
                />
              )}

              <span className="FP_Body provider-network-selected-file-name">
                {savedFile.name || "Existing file"}
              </span>

              <button
                type="button"
                className="provider-network-selected-file-remove"
                onClick={() => removeExistingFile(index)}
                title="Remove file"
                aria-label={`Remove ${savedFile.name || "existing file"}`}
              >
                <CloseIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      {!!selectedFiles.length && (
        <div className="provider-network-selected-files">
          {selectedFiles.map((selectedFile, index) => (
            <div
              key={`${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}-${index}`}
              className="provider-network-selected-file-chip"
            >
              <span className="FP_Body provider-network-selected-file-name">
                {selectedFile.name}
              </span>

              <button
                type="button"
                className="provider-network-selected-file-remove"
                onClick={() => removeNewFile(index)}
                title="Remove file"
                aria-label={`Remove ${selectedFile.name}`}
              >
                <CloseIcon />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DragDropUpload;
